// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { maxLengthAttr } from "@hardwoods/XDL/Utilities/validation";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import updateAccountSettings from "@insite/client-framework/Store/Pages/AccountSettings/Handlers/UpdateAccountSettings";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import { LinkPresentationProps } from "@insite/mobius/Link/Link";
import TextField from "@insite/mobius/TextField";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import React, { ChangeEvent } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapDispatchToProps = {
    updateAccountSettings,
};

const mapStateToProps = (state: ApplicationState) => ({
    editingAccount: state.pages.accountSettings.editingAccount,
    emailErrorMessage: state.pages.accountSettings.emailErrorMessage,
    useEmailAsUserName: getSettingsCollection(state).accountSettings.useEmailAsUserName,
    changePasswordUrl: getPageLinkByPageType(state, "ChangePasswordPage")?.url,
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface AccountSettingsUserInformationStyles {
    userInformationTitle: TypographyProps;
    userInformationGridItem?: GridItemProps;
    changePasswordLink?: LinkPresentationProps;
    changePasswordGridItem?: GridItemProps;
    userInformationGridContainer?: GridContainerProps;
}

export const userInformationStyles: AccountSettingsUserInformationStyles = {
    userInformationGridItem: {
        width: 12,
    },
    changePasswordLink: {
        css: css`
            margin-left: 10px;
            transform: translateY(-50%) scale(0.8);
            position: absolute;
            top: 28px;
            right: 15px;
        `,
        typographyProps: {
            size: 17,
        },
    },
    userInformationTitle: {
        variant: "h2",
    },
    changePasswordGridItem: {
        css: css`
            position: relative;
        `,
    },
};

const styles = userInformationStyles;
const password = "*************";
const emailRegexp = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*");

const AccountSettingsUserInformation = ({
    editingAccount,
    useEmailAsUserName,
    changePasswordUrl,
    emailErrorMessage,
    updateAccountSettings,
}: Props) => {
    if (!editingAccount) {
        return null;
    }

    const [emailErrorMsg, setEmailErrorMsg] = React.useState("");

    React.useEffect(() => {
        if (!emailErrorMessage) {
            setEmailErrorMsg("");
        }
    }, [emailErrorMessage]);

    const fullName = `${editingAccount.firstName} ${editingAccount.lastName}`;
    const requiredFieldMessage = siteMessage("Field_Required", translate("Email Address"));
    const emailFieldMessage = siteMessage("Field_Invalid", translate("Email Address"));
    const maxNumberEmailFieldMessage = siteMessage(
        "Field_Max_Number",
        translate("Email Address"),
        `${maxLengthAttr.email}`,
    );

    // Xng-Change: appling validation on email inside the component rather than the handler
    // Issue RUG-848 - site messages are not taken from admin console
    const emailChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.currentTarget.value;
        if (value === "") {
            setEmailErrorMsg(requiredFieldMessage as string);
        } else if (!emailRegexp.test(value)) {
            setEmailErrorMsg(emailFieldMessage as string);
        } else if (event.target.value.length > maxLengthAttr.email) {
            setEmailErrorMsg(maxNumberEmailFieldMessage as string);
        } else {
            setEmailErrorMsg("");
        }

        updateAccountSettings({ email: value });
    };

    const label = (
        <>
            <Typography>{translate("Password")}</Typography>
        </>
    );

    return (
        <>
            <Typography {...styles.userInformationTitle}>{translate("Your Information")}</Typography>
            <GridContainer {...styles.userInformationGridContainer}>
                {!useEmailAsUserName && (
                    <GridItem {...styles.userInformationGridItem}>
                        <TextField
                            label={translate("User Name")}
                            value={editingAccount.userName}
                            maxLength={maxLengthAttr.userName - 1}
                            data-test-selector="accountSettings_userName"
                            disabled
                        />
                    </GridItem>
                )}
                <GridItem {...styles.userInformationGridItem}>
                    <TextField
                        type="email"
                        label={translate("Email")}
                        onChange={emailChangeHandler}
                        value={editingAccount.email}
                        maxLength={maxLengthAttr.email}
                        data-test-selector="accountSettings_email"
                        required
                        error={emailErrorMsg}
                    />
                </GridItem>
                {!fullName && (
                    <GridItem {...styles.userInformationGridItem}>
                        <TextField label={translate("Full Name")} value={fullName} disabled />
                    </GridItem>
                )}
                <GridItem {...styles.userInformationGridItem} {...styles.changePasswordGridItem}>
                    <TextField
                        type="password"
                        label={label}
                        disabled
                        value={password}
                        maxLength={maxLengthAttr.password}
                    />
                    <Link {...styles.changePasswordLink} href={changePasswordUrl}>
                        {translate("Change")}
                    </Link>
                </GridItem>
            </GridContainer>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AccountSettingsUserInformation),
    definition: {
        allowedContexts: ["AccountSettingsPage"],
        group: "Account Settings",
        displayName: "User Information",
    },
};

export default widgetModule;
